import { RouteName } from '@/router/models/route-name';
import { models } from 'cesg-shared';
import {
  PermissionRouteName,
  PermissionType,
  UserRole,
  type GetMeResponse,
  type Permission,
} from 'cesg-shared/dist/models';
import { entraAuthService } from './entra-auth-service';

class AuthHelper {
  convertToPermissionRouteName(
    routeName: string | undefined,
  ): models.PermissionRouteName {
    switch (routeName) {
      case RouteName.StartPage:
        return models.PermissionRouteName.STARTPAGE;
      case RouteName.Projects:
        return models.PermissionRouteName.PROJECTS;

      // Projekt baserat
      case RouteName.Overview:
        return models.PermissionRouteName.OVERVIEW;
      case RouteName.CalculationBudget:
        return models.PermissionRouteName.BUDGET;
      case RouteName.Forecast:
        return models.PermissionRouteName.FORECAST;
      case RouteName.Documents:
        return models.PermissionRouteName.FILES;
      // ÄTOR
      case RouteName.ATA:
        return models.PermissionRouteName.ATA;
      case RouteName.ATADetails:
        return models.PermissionRouteName.ATA;
      case RouteName.ATADetailsPrint:
        return models.PermissionRouteName.ATA;

      // Fakturer
      case RouteName.Invoices:
        return models.PermissionRouteName.INVOICE;
      case RouteName.InvoiceDetails:
        return models.PermissionRouteName.INVOICE;

      // Admin
      case RouteName.Admin:
        return models.PermissionRouteName.ADMIN;
      case RouteName.AdminCreateInvoices:
        return models.PermissionRouteName.ADMIN;
      case RouteName.AdminProjectAndUsers:
        return models.PermissionRouteName.ADMIN;
      case RouteName.AdminSupplierInvoices:
        return models.PermissionRouteName.ADMIN;
      case RouteName.AdminSuppliers:
        return models.PermissionRouteName.ADMIN;
      case RouteName.AdminDocuments:
        return models.PermissionRouteName.ADMIN;
      case 'authtest':
        return models.PermissionRouteName.ADMIN;

      case undefined:
        return models.PermissionRouteName.INVALID;

      default:
        return models.PermissionRouteName.INVALID;
    }
  }

  authenticateUserRoutePermission(
    user: GetMeResponse,
    permissionRouteName: string,
  ): boolean {
    switch (permissionRouteName) {
      case PermissionRouteName.STARTPAGE.valueOf():
        return true;
      case PermissionRouteName.PROJECTS.valueOf():
        return true;
      case PermissionRouteName.ADMIN.valueOf():
        return user.role === UserRole.ADMIN;
      case PermissionRouteName.INVALID:
        return false;
    }
    return false;
  }

  authenticateProjectRoutePermission(
    user: GetMeResponse,
    projectId: string,
    permissionRouteName: models.PermissionRouteName,
  ) {
    if (user.role === UserRole.ADMIN) {
      return true;
    } else {
      const permission = user.permissions.find((permission) => {
        if (permission.projectId === projectId) {
          return permission;
        }
      });

      if (permission) {
        if (
          this.getProjectRoutePermission(permissionRouteName, permission!) !=
          PermissionType.DISABLED
        ) {
          return true;
        }
      }
    }
  }

  getProjectRoutePermission(
    permissionRouteName: PermissionRouteName,
    permission: Permission,
  ): PermissionType {
    switch (permissionRouteName) {
      // Default permission
      case PermissionRouteName.STARTPAGE.valueOf():
        return PermissionType.VIEW;
      case PermissionRouteName.PROJECTS.valueOf():
        return PermissionType.VIEW;

      case PermissionRouteName.OVERVIEW.valueOf():
        return permission.overview;
      case PermissionRouteName.BUDGET.valueOf():
        return permission.budget;
      case PermissionRouteName.FORECAST.valueOf():
        return permission.forecast;
      case PermissionRouteName.ATA.valueOf():
        return permission.ata;
      case PermissionRouteName.INVOICE.valueOf():
        return permission.invoice;
      case PermissionRouteName.FILES.valueOf():
        return permission.files;

      // Have no setting for this, default to disabled
      case PermissionRouteName.ADMIN.valueOf():
        return PermissionType.DISABLED;
    }

    return PermissionType.DISABLED;
  }

  getPermissionsFromUser(projectId: string): Permission | undefined {
    const user = entraAuthService.me.value;
    if (user) {
      return user!.permissions.find(
        (permission) => permission.projectId === projectId,
      );
    }
  }

  getPermissionFromUser(
    projectId: string,
    permissionRouteName: PermissionRouteName,
  ): PermissionType {
    const permission = this.getPermissionsFromUser(projectId);
    if (permission) {
      return this.getProjectRoutePermission(permissionRouteName, permission!);
    }
    return PermissionType.DISABLED;
  }
}
export const authHelper = new AuthHelper();
